// Shared Article Viewer Component - Used by both RoadmapViewer and ProfileView
import React, { useState } from 'react';
import { ChevronRight, CheckCircle, Loader2, Crosshair, Zap, PlayCircle, Edit2, Save, X, Sparkles } from 'lucide-react';
import type { ArticleData, RoadmapData } from '../../types';
import { VideoPlayer } from '../VideoPlayer';
import { ErrorBoundary } from '../ErrorBoundary';
import { getArticleUrl } from '../../utils/resourceFallback';
import { supabase } from '../../config/supabase';
import { CacheService } from '../../services/cacheService';

interface ArticleViewerProps {
  article: ArticleData;
  roadmap?: RoadmapData; // Optional for ProfileView articles
  dayNumberText?: string | null;
  isDark: boolean;
  onBack: () => void;
  onCompleteDay?: (dayId: string) => Promise<void>;
  onLoadNextDay?: () => Promise<void>;
  showDayBadge?: boolean;
  user?: any; // Add user prop for admin check
}

export const ArticleViewer: React.FC<ArticleViewerProps> = ({
  article,
  roadmap,
  dayNumberText,
  isDark,
  onBack,
  onCompleteDay,
  onLoadNextDay,
  showDayBadge = true,
  user
}) => {
  const [completingDay, setCompletingDay] = useState(false);
  const [loadingNextDay, setLoadingNextDay] = useState(false);
  const [dayCompleted, setDayCompleted] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [howToSteps, setHowToSteps] = useState<any[]>([]);
  const [loadingHowTo, setLoadingHowTo] = useState(false);
  
  // Admin edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    title: article.title,
    videoId: article.resources?.video?.id || '',
    description: article.subtitle || '',
    deepDive: article.deepDive || '',
    steps: article.steps?.join('\n') || '',
    example: article.example || '',
    keywords: article.keywords?.join(', ') || ''
  });

  const isAdmin = user?.email === 'dynamicnabid@gmail.com';

  const handleEditChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      // Parse steps from newline-separated text
      const steps = editForm.steps.split('\n').filter(s => s.trim());

      // Prepare resources object with updated video ID
      const resources = {
        ...(article.resources || {}),
        video: {
          ...(article.resources?.video || {}),
          id: editForm.videoId
        }
      };

      // Prepare update payload
      const updateData = {
        title: editForm.title,
        subtitle: editForm.description,
        deep_dive: editForm.deepDive,
        steps: steps,
        example: editForm.example,
        resources: resources
      };

      // Update in articles table
      const { error } = await supabase
        .from('articles')
        .update(updateData)
        .eq('id', article.id);

      if (error) {
        alert('❌ Error saving article: ' + error.message);
        setIsSaving(false);
        return;
      }

      // Invalidate cache
      CacheService.invalidateCategory('article');

      alert('✅ Article updated successfully!');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving article:', error);
      alert('❌ An error occurred while saving the article');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      title: article.title,
      videoId: article.resources?.video?.id || '',
      description: article.subtitle || '',
      deepDive: article.deepDive || '',
      steps: article.steps?.join('\n') || '',
      example: article.example || '',
      keywords: article.keywords?.join(', ') || ''
    });
    setIsEditMode(false);
  };

  // Debug: Log article data when it changes
  React.useEffect(() => {
    console.log('📄 ArticleViewer loaded with article:', {
      id: article.id,
      title: article.title,
      example: article.example,
      exampleType: article.exampleType,
      deepDive: article.deepDive?.substring(0, 50),
      concepts: article.technicalConcepts?.length || 0,
      steps: article.steps?.length || 0,
      resources: !!article.resources,
      allFields: Object.keys(article)
    });
  }, [article]);

  const parseFormattedContent = (text: string) => {
    const lines = text.split('\n');
    const elements: any[] = [];
    let codeBlock = false;
    let codeContent = '';

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('```')) {
        if (codeBlock) {
          elements.push({
            key: `code-${idx}`,
            type: 'code',
            content: codeContent.trim()
          });
          codeContent = '';
          codeBlock = false;
        } else {
          codeBlock = true;
        }
      } else if (codeBlock) {
        codeContent += line + '\n';
      } else if (trimmed.startsWith('## ')) {
        elements.push({
          key: `h2-${idx}`,
          type: 'h2',
          content: trimmed.substring(3)
        });
      } else if (trimmed.startsWith('### ')) {
        elements.push({
          key: `h3-${idx}`,
          type: 'h3',
          content: trimmed.substring(4)
        });
      } else if (trimmed.startsWith('- ')) {
        elements.push({
          key: `li-bullet-${idx}`,
          type: 'li-bullet',
          content: trimmed.substring(2)
        });
      } else if (trimmed.length > 0) {
        elements.push({
          key: `p-${idx}`,
          type: 'p',
          content: trimmed
        });
      }
    });

    return elements;
  };

  const formattedHowToSteps = howToSteps.length > 0 ? parseFormattedContent(howToSteps.join('\n')) : [];

  return (
    <>
    <div className="animate-in fade-in space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-red-500 font-semibold text-sm group hover:text-red-600 transition-colors"
        >
          <ChevronRight className="rotate-180 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </button>
      </div>

      {/* Article Content */}
      <div className={`rounded-3xl p-6 md:p-8 border ${
        isDark ? 'bg-white/[0.03] border-white/5' : 'bg-white border-zinc-200/80 shadow-xl'
      }`}>
        {/* Header */}
        <div className="mb-6 space-y-2">
          {showDayBadge && dayNumberText && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border border-red-400/40 bg-red-500/10 text-red-600 dark:text-red-300 dark:border-red-500/50 dark:bg-red-500/15">
              <span className="text-xs uppercase tracking-wide">Day</span>
              <span className="text-base">{dayNumberText}</span>
            </div>
          )}
          <h3 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{article.title}</h3>
          <p className={`text-lg ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{article.subtitle}</p>
        </div>

        {/* ADMIN EDIT BUTTON */}
        {isAdmin && !isEditMode && (
          <button
            onClick={() => setIsEditMode(true)}
            className={`mb-6 flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              isDark
                ? 'bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/30 hover:border-red-500/80'
                : 'bg-red-100 border border-red-300 text-red-600 hover:bg-red-200 hover:border-red-400'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            Edit Article
          </button>
        )}

        {/* EDIT MODE FORM */}
        {isAdmin && isEditMode && (
          <div className={`mb-8 p-6 rounded-xl border-2 ${
            isDark
              ? 'bg-red-500/5 border-red-500/30'
              : 'bg-red-50/50 border-red-200'
          }`}>
            <h4 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Edit Article Content
            </h4>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Title
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => handleEditChange('title', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Video ID */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  YouTube Video ID
                </label>
                <input
                  type="text"
                  value={editForm.videoId}
                  onChange={(e) => handleEditChange('videoId', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                  placeholder="e.g., dQw4w9WgXcQ"
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Subtitle/Description
                </label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Deep Dive */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Deep Dive (Full Explanation)
                </label>
                <textarea
                  value={editForm.deepDive}
                  onChange={(e) => handleEditChange('deepDive', e.target.value)}
                  rows={6}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Steps */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Learning Steps (one per line)
                </label>
                <textarea
                  value={editForm.steps}
                  onChange={(e) => handleEditChange('steps', e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none font-mono ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                  placeholder="Step 1&#10;Step 2&#10;Step 3"
                />
              </div>

              {/* Example */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Example/Code Sample
                </label>
                <textarea
                  value={editForm.example}
                  onChange={(e) => handleEditChange('example', e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none font-mono ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Keywords */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Keywords (comma-separated)
                </label>
                <input
                  type="text"
                  value={editForm.keywords}
                  onChange={(e) => handleEditChange('keywords', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                    isDark
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                    isDark
                      ? 'bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50'
                      : 'bg-zinc-300 text-zinc-900 hover:bg-zinc-400 disabled:opacity-50'
                  }`}
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="mb-8 space-y-4">
          <h4 className={`text-xl font-bold flex items-center gap-2 mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            <PlayCircle className="w-5 h-5 text-red-500" />
            Video Tutorial
          </h4>
          <VideoPlayer 
            article={article} 
            isDark={isDark}
          />
          {article.resources?.video?.url && (
            <a
              href={article.resources.video.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`block p-4 rounded-lg border-2 transition-all hover:shadow-lg ${
                isDark
                  ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/60 hover:bg-red-500/15'
                  : 'bg-red-50 border-red-200 hover:border-red-400 hover:bg-red-100/50'
              }`}
            >
              <p className="font-semibold text-red-600 hover:text-red-700 text-sm mb-1">
                📺 Open in YouTube
              </p>
              <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                Watch the full tutorial and explore related videos
              </p>
            </a>
          )}
        </div>

        {/* Content */}
        <div className={`prose prose-lg max-w-none mb-8 ${isDark ? 'prose-invert' : ''}`}>
          {/* Overview */}
          <h4 className={`text-xl font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            <Crosshair className="w-5 h-5 text-red-500" />
            Overview
          </h4>
          {article.deepDive ? (
            <p className={`text-base leading-relaxed mb-6 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{article.deepDive}</p>
          ) : (
            <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800/50 text-zinc-400' : 'bg-zinc-100 text-zinc-600'}`}>
              <p>No overview available for this article yet.</p>
              <p className="text-sm mt-2">⏳ Overview content will be added soon</p>
            </div>
          )}

          {/* Technical Concepts */}
          {article.technicalConcepts && article.technicalConcepts.length > 0 && (
            <>
              <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Zap className="w-5 h-5 text-red-500" />
                Key Concepts
              </h4>
              <div className="grid gap-3 mb-6">
                {article.technicalConcepts?.map((concept, idx) => (
                  <div key={idx} className={`p-4 rounded-xl border ${
                    isDark ? 'bg-white/5 border-white/10' : 'bg-zinc-50 border-zinc-200'
                  }`}>
                    <strong className="text-red-500 font-semibold">{concept.term}:</strong> <span className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{concept.explanation}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Steps */}
          {article.steps && article.steps.length > 0 && (
            <>
              <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Crosshair className="w-5 h-5 text-red-500" />
                Learning Steps
              </h4>
              <ol className="space-y-2 mb-6">
                {article.steps?.map((step, idx) => (
                  <li key={idx} className={`flex gap-3 p-3 rounded-lg ${
                    isDark ? 'bg-white/5' : 'bg-zinc-50'
                  }`}>
                    <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <span className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{step}</span>
                  </li>
                ))}
              </ol>
            </>
          )}

          {/* Example Section */}
          {article.example && (
            <>
              <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Zap className="w-5 h-5 text-red-500" />
                {article.exampleType === 'code' ? 'Code Example' : 'Real-Life Example'}
              </h4>
              <div className={`p-4 rounded-xl border ${
                isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'
              }`}>
                {article.exampleType === 'code' ? (
                  <pre className={`text-sm overflow-x-auto ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                    <code>{article.example}</code>
                  </pre>
                ) : (
                  <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{article.example}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Learning Resources Section */}
        {article.resources && (
          <div className={`rounded-2xl p-6 border-2 border-red-500/30 ${
            isDark ? 'bg-red-500/5' : 'bg-red-50/50'
          }`}>
            <h4 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
              <PlayCircle className="w-5 h-5" />
              Learning Resources
            </h4>

            <div className="space-y-6">
              {/* Articles */}
              {article.resources.articles && article.resources.articles.length > 0 && (
                <div>
                  <h5 className={`text-sm font-semibold mb-3 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    📄 High-Quality Articles
                  </h5>
                  <div className="space-y-3">
                    {article.resources.articles.map((res, idx) => {
                      const { url, isFallback } = getArticleUrl(
                        res.url,
                        res.title,
                        article.topic,
                        roadmap?.goal
                      );
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`block p-4 rounded-lg border transition-all hover:shadow-lg ${
                            isFallback
                              ? isDark
                                ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/10'
                                : 'bg-amber-50 border-amber-200 hover:border-amber-300 hover:bg-amber-100/30'
                              : isDark
                                ? 'bg-white/5 border-white/10 hover:border-red-500/50 hover:bg-white/10'
                                : 'bg-white border-zinc-200 hover:border-red-300 hover:bg-red-50/30'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className={`font-semibold text-sm break-words flex-1 ${
                              isFallback
                                ? isDark ? 'text-amber-400' : 'text-amber-600'
                                : 'text-red-600 hover:text-red-700'
                            }`}>
                              {res.title}
                            </p>
                            {isFallback && (
                              <span className={`text-xs px-2 py-1 rounded font-semibold whitespace-nowrap ${
                                isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-200 text-amber-800'
                              }`}>
                                Search
                              </span>
                            )}
                          </div>
                          {res.preview && (
                            <p className={`text-xs leading-relaxed mb-2 break-words ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                              {res.preview}
                            </p>
                          )}
                          <p className={`text-xs break-all ${
                            isFallback
                              ? isDark ? 'text-amber-500/70' : 'text-amber-600/70'
                              : isDark ? 'text-zinc-500' : 'text-zinc-400'
                          }`}>
                            🔗 {isFallback ? 'Google Search →' : ''} {url.substring(0, 60)}...
                          </p>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons - Only show if from roadmap */}
        {onCompleteDay && onLoadNextDay && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-white/10">
            <button
              onClick={async () => {
                if (dayCompleted) return;
                setCompletingDay(true);
                try {
                  if (article?.dayId) {
                    await onCompleteDay(article.dayId);
                    setDayCompleted(true);
                  }
                } finally {
                  setCompletingDay(false);
                }
              }}
              disabled={completingDay || loadingNextDay || dayCompleted}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                dayCompleted
                  ? isDark
                    ? 'bg-green-500/30 border border-green-400 text-green-300 scale-105 shadow-lg shadow-green-500/20'
                    : 'bg-green-200/60 border border-green-400 text-green-800 scale-105 shadow-lg shadow-green-400/20'
                  : isDark
                    ? 'bg-green-600/20 border border-green-500/50 text-green-400 hover:bg-green-600/30 hover:border-green-500/80 disabled:opacity-50'
                    : 'bg-green-100 border border-green-300 text-green-700 hover:bg-green-200 hover:border-green-400 disabled:opacity-50'
              }`}
            >
              {dayCompleted ? (
                <div className="flex items-center justify-center gap-2 animate-in slide-in-from-left">
                  <CheckCircle className="w-5 h-5 animate-pulse" />
                  <span>✨ You have completed {dayNumberText ? `Day ${dayNumberText}` : 'this lesson'}</span>
                </div>
              ) : completingDay ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Marking as completed...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Mark as completed {dayNumberText ? `Day ${dayNumberText}` : 'this lesson'}
                </>
              )}
            </button>

            <button
              onClick={async () => {
                setLoadingNextDay(true);
                try {
                  await onLoadNextDay();
                } finally {
                  setLoadingNextDay(false);
                }
              }}
              disabled={completingDay || loadingNextDay}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                isDark
                  ? 'bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/30 hover:border-red-500/80 disabled:opacity-50'
                  : 'bg-red-100 border border-red-300 text-red-700 hover:bg-red-200 hover:border-red-400 disabled:opacity-50'
              }`}
            >
              {loadingNextDay ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading Next Day...
                </>
              ) : (
                <>
                  Next Article
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ArticleViewer;
